// src/admin/AdminSendOffers.jsx
import { useEffect, useState } from "react";
import { collection, getDocs, deleteDoc, doc, setDoc, serverTimestamp, orderBy, query } from "../services/mysqlStore";
import { db } from "../services/mysqlStore";
import API_BASE, { getAuthToken, apiRequest } from "../services/apiClient";

const EMAIL_REGEX = /[^\s,;"'<>]+@[^\s,;"'<>]+\.[^\s,;"'<>]+/g;

export default function AdminSendOffers() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selected, setSelected]       = useState({}); // { [id]: true }

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [posterUrl, setPosterUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const [sending, setSending]   = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: 0 });
  const [finished, setFinished] = useState(false);

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState("");

  async function fetchSubscribers() {
    setLoading(true);
    try {
      const q    = query(collection(db, "newsletterSubscribers"), orderBy("subscribedAt", "desc"));
      const snap = await getDocs(q);
      const newsletterList = snap.docs.map(d => ({ id: d.id, email: d.data().email, subscribedAt: d.data().subscribedAt, source: d.data().source === "csv-import" ? "csv-import" : "newsletter" }));

      let userList = [];
      try {
        const users = await apiRequest("/admin/users");
        userList = users.filter(u => u.email).map(u => ({ id: `user-${u.id}`, email: u.email, subscribedAt: u.createdAt, source: "user" }));
      } catch (err) { console.error("Failed to load registered users:", err); }

      const seenEmails = new Set();
      const list = [...newsletterList, ...userList].filter(item => {
        const email = String(item.email).toLowerCase();
        if (seenEmails.has(email)) return false;
        seenEmails.add(email);
        return true;
      });

      setSubscribers(list);
      // Select all by default
      const sel = {};
      list.forEach(s => { sel[s.id] = true; });
      setSelected(sel);
    } catch (err) {
      console.error("Failed to load subscribers:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchSubscribers(); }, []);

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const allSelected = subscribers.length > 0 && selectedCount === subscribers.length;

  function toggleOne(id) {
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleAll() {
    if (allSelected) {
      setSelected({});
    } else {
      const sel = {};
      subscribers.forEach(s => { sel[s.id] = true; });
      setSelected(sel);
    }
  }

  async function deleteSubscriber(id) {
    if (!window.confirm("Remove this email from the subscriber list?")) return;
    await deleteDoc(doc(db, "newsletterSubscribers", id));
    fetchSubscribers();
  }

  async function handleImportFile(file) {
    if (!file) return;
    setImporting(true);
    setImportResult("");
    try {
      const text = await file.text();
      const found = text.match(EMAIL_REGEX) || [];
      const uniqueEmails = [...new Set(found.map(e => e.trim().toLowerCase()))];
      if (!uniqueEmails.length) {
        setImportResult("No email addresses were found in that file.");
        return;
      }
      const existing = new Set(subscribers.map(s => s.email.toLowerCase()));
      const newEmails = uniqueEmails.filter(e => !existing.has(e));
      for (const email of newEmails) {
        await setDoc(doc(db, "newsletterSubscribers", email), { email, subscribedAt: serverTimestamp(), source: "csv-import" });
      }
      setImportResult(`Imported ${newEmails.length} new email${newEmails.length !== 1 ? "s" : ""} (${uniqueEmails.length - newEmails.length} already on the list, saved for later use).`);
      fetchSubscribers();
    } catch (err) {
      setImportResult(`error: ${err.message}`);
    } finally {
      setImporting(false);
    }
  }

  async function handleSendOffers() {
    const targets = subscribers.filter(s => selected[s.id]);
    if (targets.length === 0) return;
    if (!subject.trim() || !message.trim()) {
      window.alert("Please enter both a subject and a message before sending.");
      return;
    }
    if (!window.confirm(`Send this offer to ${targets.length} subscriber(s)?`)) return;

    setSending(true);
    setFinished(false);
    setProgress({ done: 0, total: targets.length, failed: 0 });
    try {
      const result = await apiRequest("/admin/newsletter/send", { method: "POST", body: JSON.stringify({ recipients: targets.map(target => target.email), subject, message, posterUrl }) });
      setProgress({ done: result.sent, total: targets.length, failed: targets.length - result.sent });
      setFinished(true);
    } catch (err) {
      setProgress({ done: 0, total: targets.length, failed: targets.length });
      window.alert(err.message);
    } finally { setSending(false); }
  }

  async function uploadPoster(file) {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData(); body.append("file", file); body.append("path", "offers");
      const response = await fetch(`${API_BASE}/admin/files/upload`, { method: "POST", headers: { Authorization: `Bearer ${getAuthToken()}` }, body });
      const contentType = response.headers.get("content-type") || "";
      const data = contentType.includes("application/json") ? await response.json() : { error: `Upload endpoint returned HTTP ${response.status}. Restart the API and try again.` };
      if (!response.ok) throw new Error(data.error || "Poster upload failed");
      setPosterUrl(data.url);
    } catch (err) { window.alert(err.message); }
    finally { setUploading(false); }
  }

  return (
    <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>

      {/* Left: Compose */}
      <div className="adm-card" style={{ flex: "1 1 380px", padding: 24 }}>
        <div className="adm-card-header" style={{ padding: 0, border: "none", marginBottom: 16 }}>
          <span className="adm-card-title">Compose Offer</span>
        </div>

        <div className="adm-form">
          <div className="adm-form-group">
            <label>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="e.g. 20% Off All Business Cards This Week!"
              disabled={sending}
            />
          </div>

          <div className="adm-form-group">
            <label>Poster / offer image</label>
            <label
              style={{ border: `2px dashed ${dragging ? "var(--cyan)" : "rgba(255,255,255,0.18)"}`, background: dragging ? "rgba(0,170,221,0.08)" : "transparent", borderRadius: 8, padding: 16, textAlign: "center", cursor: "pointer", display: "block" }}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); uploadPoster(e.dataTransfer.files?.[0]); }}
            >
              <input type="file" accept="image/*" hidden onChange={e => uploadPoster(e.target.files?.[0])} />
              {posterUrl ? <img src={posterUrl} alt="Offer preview" style={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain" }} /> : <span style={{ color: "var(--adm-muted)" }}>{uploading ? "Uploading..." : "Drag a poster here or click to choose one"}</span>}
            </label>
            {posterUrl && <button type="button" className="adm-btn adm-btn-sm" onClick={() => setPosterUrl("")}>Remove poster</button>}
            <div style={{ border: "1px solid var(--adm-border)", borderRadius: 8, padding: 16, background: "#fff", color: "#202124" }}>
              <div style={{ borderBottom: "1px solid #ddd", paddingBottom: 8, marginBottom: 12, fontWeight: 700 }}>{subject || "Offer subject preview"}</div>
              <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{message || "Your offer message will appear here."}</div>
              {posterUrl && <img src={posterUrl} alt="Offer preview" style={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain", display: "block", marginTop: 12 }} />}
              <div style={{ color: "#777", fontSize: 11, marginTop: 16 }}>Unsubscribe link will be included automatically.</div>
            </div>
          </div>

          <div className="adm-form-group">
            <label>Message</label>
            <textarea
              rows={10}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={"Hi there,\n\nWe've got an exclusive offer just for you...\n\nBest regards,\nCircuit Printing Team"}
              disabled={sending}
            />
          </div>

          <button
            className="adm-btn adm-btn-primary"
            onClick={handleSendOffers}
            disabled={sending || selectedCount === 0}
            style={{ opacity: (sending || selectedCount === 0) ? 0.5 : 1, cursor: (sending || selectedCount === 0) ? "not-allowed" : "pointer" }}
          >
            {sending ? `Sending... (${progress.done}/${progress.total})` : `Send to Selected (${selectedCount})`}
          </button>

          {finished && (
            <p style={{ fontSize: 13, marginTop: 4, color: progress.failed > 0 ? "var(--yellow)" : "#4ade80", fontWeight: 600 }}>
              {progress.failed > 0
                ? `Done — sent ${progress.total - progress.failed}/${progress.total}, ${progress.failed} failed.`
                : `✅ Sent successfully to all ${progress.total} subscriber(s)!`}
            </p>
          )}
        </div>
      </div>

      {/* Right: Subscriber List */}
      <div className="adm-card" style={{ flex: "1 1 420px" }}>
        <div className="adm-card-header" style={{ flexDirection: "column", alignItems: "stretch", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="adm-card-title">Import from CSV / Excel export</span>
          </div>
          <p style={{ color: "var(--adm-muted)", fontSize: 12, margin: 0 }}>
            Upload a .csv file with one email per line, or a comma-separated list — any email addresses found anywhere in the file are picked up automatically.
            Save an Excel sheet as "CSV (Comma delimited)" first if your list is in .xlsx. Imported emails are saved permanently so they're ready to reuse next time.
          </p>
          <pre style={{ background: "rgba(255,255,255,0.05)", border: "1px solid var(--adm-border)", borderRadius: 6, padding: 10, fontSize: 11, color: "var(--adm-muted)", margin: 0, whiteSpace: "pre-wrap" }}>
{`Example file format (email.csv):
email,name
jane@example.com,Jane Doe
john@example.com,John Smith`}
          </pre>
          <label className="adm-btn adm-btn-sm" style={{ cursor: "pointer", textAlign: "center", background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)" }}>
            {importing ? "Importing..." : "📂 Choose CSV file"}
            <input type="file" accept=".csv,.txt" hidden disabled={importing} onChange={e => { handleImportFile(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          {importResult && (
            <span style={{ fontSize: 12, color: importResult.startsWith("error:") ? "#f87171" : "#4ade80" }}>
              {importResult.replace(/^error:\s*/, "")}
            </span>
          )}
        </div>
        <div className="adm-card-header">
          <span className="adm-card-title">Recipients ({subscribers.length})</span>
          {subscribers.length > 0 && (
            <button className="adm-btn adm-btn-sm" onClick={toggleAll} style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)" }}>
              {allSelected ? "Deselect All" : "Select All"}
            </button>
          )}
        </div>

        {loading ? (
          <p style={{ padding: 24, color: "rgba(255,255,255,0.3)", fontSize: 13 }}>Loading recipients...</p>
        ) : subscribers.length === 0 ? (
          <p style={{ padding: 24, color: "rgba(255,255,255,0.3)", fontSize: 13 }}>No recipients yet. Newsletter signups and registered customer accounts will appear here.</p>
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>Email</th>
                  <th>Source</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map(s => (
                  <tr key={s.id}>
                    <td>
                      <input type="checkbox" checked={!!selected[s.id]} onChange={() => toggleOne(s.id)} disabled={sending} />
                    </td>
                    <td>{s.email}</td>
                    <td style={{ color: "rgba(255,255,255,0.45)", fontSize: 12 }}>
                      <span className={`adm-badge ${s.source === "user" ? "adm-badge-done" : "adm-badge-new"}`}>
                        {s.source === "user" ? "Registered user" : s.source === "csv-import" ? "CSV Import" : "Newsletter"}
                      </span>
                    </td>
                    <td>
                      {s.source !== "user" && (
                        <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => deleteSubscriber(s.id)} disabled={sending}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
